import React, { useEffect } from 'react';
import type { Question } from '../../../types';
import { Image } from '../../ui/image';
import { CustomRadarChart } from '../../RadarChart';
import { registerQuestionRenderer, type QuestionRendererComponent } from './registry';

const resolveImageSource = (question: Question) => {
  if (question.media?.url) {
    return question.media.url;
  }

  if (typeof question.image_url === 'string' && question.image_url.length > 0) {
    return question.image_url;
  }

  return null;
};

export const RadarChartRenderer: QuestionRendererComponent = ({
  question,
  existingResponse,
  onDraftChange,
  onHasUserInputChange,
  onValidityChange,
  onExtrasChange,
}) => {
  void onExtrasChange;
  const questionId = question?.id ?? existingResponse?.question ?? -1;

  useEffect(() => {
    if (questionId < 0) {
      onDraftChange(null);
      return;
    }

    onDraftChange(existingResponse ?? { question: questionId });
    onHasUserInputChange?.(true);
    onValidityChange?.(true);
  }, [existingResponse, onDraftChange, onHasUserInputChange, onValidityChange, questionId]);

  const imageSource = resolveImageSource(question as any);
  const definitionKey =
    typeof question.metadata?.radar_definition_key === 'string'
      ? question.metadata.radar_definition_key.trim()
      : '';

  return (
    <div className="flex w-full flex-col items-center gap-3 bg-transparent p-3">
      {imageSource ? (
        <div className="mb-3 max-w-full">
          <Image imageUrl={imageSource} />
        </div>
      ) : null}
      {definitionKey ? (
        <div className="h-[500px] w-full max-w-[90vw] min-w-[600px]">
          <CustomRadarChart definitionKey={definitionKey} />
        </div>
      ) : (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Radar chart definition is not configured.
        </div>
      )}
    </div>
  );
};

registerQuestionRenderer(['radar_chart'], RadarChartRenderer);
